// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — markShop.js   ·   THE MARKETPLACE, acquisition layer
// ────────────────────────────────────────────────────────────────────
// Mystic Ally's till. The ONLY code path by which a Mark variant
// changes hands. The UI never writes; it asks this file and renders
// the answer.
//
// ── THE THREE STOREFRONT RULES (ie-cross-game-economy) ──────────────
//   1. READ THE PRICE FROM THE CATALOGUE, NEVER FROM THE CALLER.
//      A client that hands you a price is describing what it WANTS to
//      pay. That is the entire attack shape. `acquireVariant` takes an
//      id and looks the price up itself; there is no price parameter.
//   2. REFUSING IS AN ANSWER, NOT AN ERROR. Every path resolves
//      { ok:false, reason } — never throws. A business rule caught in
//      a catch block reads as a fault in the logs forever.
//   3. STAMP WHAT WAS PAID. `paidDC` plus `listPriceDC` on the record.
//      One field each, and it is what later proves every acquisition
//      settled legitimately.
//
// ── ⚠ THE TESTING TILL — caP'n's ruling, 2026-08-25 ─────────────────
//
//   **"Make them 1 Divine Crest. Everything in testing until I say so
//     costs 1."**
//
// So: every shelf is OPEN and every plate costs ◆1, whatever its
// sticker says. `TESTING` below is the switch, and it is ONE const.
//
// ⚠ THE TRUST GAP IS STILL REAL AND IS NOT CLOSED BY THIS. Firebase
// rules validate `divineCrests` as "a non-negative number that did not
// jump too far" and nothing more, so any authed player can address that
// field in their own console. **A SHOP READS A BALANCE IT CANNOT
// BELIEVE.** Real priced vending still waits on server authority (Cloud
// Functions, `mass app/ie-functions/`, Blaze) — IE_CURRENCY_HARDENING.
//
// Taking that risk knowingly, at ◆1, on cosmetics, during testing, is a
// completely different bet from taking it at ◆90 on the top shelf — and
// it buys the thing a free shelf could never buy: the SPEND path
// exercised end to end before the day it matters. When testing ends,
// flip TESTING to false and the real sticker prices apply.
//
// The charge is a delta `runTransaction` on the leaf, never a
// read-compute-write, and it ABORTS rather than going negative.
//
// ── FIREBASE (no new node, no new rule) ─────────────────────────────
//   profiles/{uid}/unlockedCosmetics/mark_variant___{id}   append-only
//   profiles/{uid}/mark/variant                            owner-write
// Both sit inside `profiles/{uid}`, which is already owner-write with
// no `$extra:false` gate. Adding the Marketplace costs zero rules work.
// ════════════════════════════════════════════════════════════════════

import { ref, onValue, get, update, runTransaction } from "firebase/database";
import { db, auth } from "./firebase";
import {
  MARK_VARIANTS, variantsOnShelf, DEFAULT_VARIANT,
} from "./markVariants";
import { setWornVariant } from "./markData";

// ── THE TILL ────────────────────────────────────────────────────────
// One const. Flip a row the day server authority lands. Do not scatter
// this decision across the UI — a shelf that is open in the shop and
// closed in the writer is how a player gets a refusal after a receipt.
// ⭐ THE TESTING SWITCH. One const. Flip to false and every sticker
// price in markVariants.js applies exactly as written.
export const TESTING = true;
export const TESTING_PRICE_DC = 1;

export const VENDING = {
  fable:     "open",
  legendary: "open",
  mythical:  "open",
};

export const LOCK_REASON =
  "Bonnie can show you the plate, but she won't take a Crest she " +
  "cannot verify — and she says that is the only honest way to run a counter.";

export const NO_FUNDS_REASON =
  "Not enough Crests. The Well is up the boardwalk, and it does not judge.";

// What a plate ACTUALLY costs right now. Every price the shop shows and
// every price it charges comes through this one function, so the tag and
// the till can never disagree — which is the specific way a storefront
// lies to a player.
export function priceOf(v) {
  const item = typeof v === "string" ? MARK_VARIANTS[v] : v;
  if (!item) return null;
  if (item.id === DEFAULT_VARIANT) return 0;
  return TESTING ? TESTING_PRICE_DC : (Number(item.priceDC) || 0);
}

export const OWNED_PREFIX = "mark_variant___";
const ownKey = (id) => `${OWNED_PREFIX}${id}`;

// ── READ ────────────────────────────────────────────────────────────
// Which variants this player owns. The Classic is owned by everyone,
// always, and is not stored — a default nobody can lose is better than
// a row every account has to be backfilled with.
export function listenToOwnedVariants(uid, cb) {
  if (!uid) { cb({ [DEFAULT_VARIANT]: { source: "default" } }); return () => {}; }
  const r = ref(db, `profiles/${uid}/unlockedCosmetics`);
  const off = onValue(r, (snap) => {
    const all = snap.val() || {};
    const out = { [DEFAULT_VARIANT]: { source: "default" } };
    for (const [k, v] of Object.entries(all)) {
      if (!k.startsWith(OWNED_PREFIX)) continue;
      const id = k.slice(OWNED_PREFIX.length);
      if (MARK_VARIANTS[id]) out[id] = v || true;
    }
    cb(out);
  });
  return () => off();
}

export const ownsVariant = (owned, id) =>
  id === DEFAULT_VARIANT || !!(owned && owned[id]);

// ── WRITE · ACQUIRE ─────────────────────────────────────────────────
// Takes an ID. Never a price. Never a shelf. Never a "free" flag.
export async function acquireVariant(uid, variantId) {
  if (!uid || auth.currentUser?.uid !== uid) {
    return { ok: false, reason: "not_you", message: "You have to be signed in as yourself to buy anything." };
  }
  const v = MARK_VARIANTS[variantId];
  if (!v) return { ok: false, reason: "no_such_item", message: "Bonnie has never stocked that." };
  if (variantId === DEFAULT_VARIANT) {
    return { ok: false, reason: "already_owned", message: "Everyone already has the Classic. That is rather the point of it." };
  }

  const shelfState = VENDING[v.shelf] || "locked";
  if (shelfState !== "open") {
    return { ok: false, reason: "till_closed", message: LOCK_REASON };
  }

  const ownedSnap = await get(ref(db, `profiles/${uid}/unlockedCosmetics`));
  const owned = {};
  for (const k of Object.keys(ownedSnap.val() || {})) {
    if (k.startsWith(OWNED_PREFIX)) owned[k.slice(OWNED_PREFIX.length)] = true;
  }
  if (owned[variantId]) {
    return { ok: false, reason: "already_owned", message: "It is already on your plate rack." };
  }

  // ⚠ NO cross-item Mythical quota. caP'n overruled one on 2026-08-25:
  // rarity is a property of the item's supply, never a cap on the
  // player. Owning a Lord must not stop you buying the next one.

  // ⭐ THE PRICE COMES FROM HERE. There is no price parameter on this
  // function and there must never be one — a client that hands you a
  // price is describing what it WANTS to pay, and that is the whole
  // attack shape.
  const listPriceDC = Number(v.priceDC) || 0;
  const cost = priceOf(v);

  // ── THE CHARGE ────────────────────────────────────────────────────
  // A DELTA on the LEAF, never a read-compute-write on the profile. Two
  // writes landing at once is exactly how April 2026 wiped a vault.
  // The transaction ABORTS (returns undefined) rather than going
  // negative, so "you cannot afford it" is decided by the database and
  // not by a number this client read a moment ago.
  let charged = 0;
  if (cost > 0) {
    let aborted = false;
    let took = false;
    try {
      const res = await runTransaction(
        ref(db, `profiles/${uid}/divineCrests`),
        (cur) => {
          // ⚠ unloaded reads null on the first run — hand it back so the
          // handler re-runs on the server's value (2026-09-16 empty-cache fix)
          aborted = false; took = false;
          if (cur === null || cur === undefined) return cur;
          if (cur < cost) { aborted = true; return; }   // abort, do not clamp
          took = true;
          return cur - cost;
        }
      );
      if (aborted || !res.committed || !took) {
        return { ok: false, reason: "insufficient", message: NO_FUNDS_REASON };
      }
      charged = cost;
    } catch (e) {
      return { ok: false, reason: "charge_failed", message: "The Crest wouldn't leave your hand. Try again." };
    }
  }

  // ── THE GRANT ─────────────────────────────────────────────────────
  // ⚠ A PURCHASE INVERTS "grant before flag". That law protects a
  // player from losing a reward they earned; here the Crest is already
  // gone, so the failure to defend against is a paid-for plate that
  // never arrived. If the grant fails we REFUND, by the same delta
  // route, and say so.
  try {
    await update(ref(db, `profiles/${uid}/unlockedCosmetics`), {
      [ownKey(variantId)]: {
        source: "pier:marketplace",       // namespaced provenance, per §3.5
        vendor: "mystic_ally",
        shelf: v.shelf,
        paidDC: charged,                   // stamp what was paid…
        listPriceDC,                       // …and what it was worth
        testing: TESTING || null,          // …and that this was a test price
        at: Date.now(),
      },
    });
  } catch (e) {
    if (charged > 0) {
      try {
        await runTransaction(ref(db, `profiles/${uid}/divineCrests`), (cur) => (cur || 0) + charged);
      } catch (e2) {
        return {
          ok: false, reason: "refund_failed",
          message: `The plate didn't arrive and ◆${charged} could not be returned automatically. Tell caP'n — nothing here is lost, it just needs a hand.`,
        };
      }
    }
    return { ok: false, reason: "write_failed", message: "The plate slipped off the counter — your Crest is back. Try again." };
  }
  return { ok: true, variantId, paidDC: charged, listPriceDC };
}

// ── WRITE · EQUIP ───────────────────────────────────────────────────
// Cosmetics are equip-anywhere / appear-everywhere (§3.5). This writes
// the self-authored `mark` node and nothing else. It cannot move
// currency: there is no currency write path in this function.
export async function equipVariant(uid, variantId) {
  if (!uid || auth.currentUser?.uid !== uid) {
    return { ok: false, reason: "not_you", message: "You can only re-plate your own Mark." };
  }
  const v = MARK_VARIANTS[variantId];
  if (!v) return { ok: false, reason: "no_such_item", message: "No such plate." };
  if (variantId !== DEFAULT_VARIANT) {
    const snap = await get(ref(db, `profiles/${uid}/unlockedCosmetics/${ownKey(variantId)}`));
    if (!snap.exists()) {
      return { ok: false, reason: "not_owned", message: "You do not own that plate yet." };
    }
  }
  // ⚠ ONE WRITER. The equip write lives in markData.js because the Mark
  // wheel (any title, own player) and this shop (Pierre only) must not
  // be two different ways of putting a plate on. This function is the
  // shop's OWNERSHIP CHECK in front of that one writer, and nothing
  // more — a second `update` here would be a second truth about what
  // equipping means.
  const res = await setWornVariant(uid, variantId);
  if (!res.ok) {
    return { ok: false, reason: res.reason, message: "The Mark would not take the plate. Try again." };
  }
  return { ok: true, variantId };
}

// ── THE STOCK LIST ──────────────────────────────────────────────────
// What Mystic Ally has on each shelf, in shelf order, with the till's
// state attached so the UI never has to decide policy for itself.
export function stockFor(shelf) {
  return {
    shelf,
    state: VENDING[shelf] || "locked",
    items: variantsOnShelf(shelf),
  };
}
